import { EntityId } from '@reduxjs/toolkit';
import { RootState } from 'initializers/types';
import createCachedSelector from 're-reselect';
import { createSelector } from 'reselect';
import Role from 'resources/Role';

import { getCurrentLocationId } from 'selectors/session';

import {
  selectAllJobs,
  selectJobAttribute,
} from 'features/scheduleBuilder/slices/jobs/slice';
import { selectColorMappingVersion } from 'features/scheduleBuilder/slices/session/slice';

import { SLICE_NAME } from '../constants';
import { getDefaultShiftColor } from '../helpers';

const selectScheduleBuilderViewState = (state: RootState) =>
  state.get(SLICE_NAME) || {};

export const selectCurrentDateRange = createSelector(
  selectScheduleBuilderViewState,
  slice => slice.currentDateRange
);

export const selectCurrentDateRangeStart = createSelector(
  selectCurrentDateRange,
  currentDateRange => currentDateRange?.start
);

export const selectCurrentDateRangeEnd = createSelector(
  selectCurrentDateRange,
  currentDateRange => currentDateRange?.end
);

const getCurrentLocationIdForSchedule = (state: RootState) =>
  String(
    getCurrentLocationId(state) || state.getIn(['session'])?.currentLocation?.id
  );

export const selectJobsForCurrentLocation = createSelector(
  getCurrentLocationIdForSchedule,
  selectAllJobs,
  (currentLocationId, jobs) =>
    jobs?.filter(job => job.id && job.location?.id === currentLocationId) || []
);

export const selectJobIdUserIdMap = createSelector(
  selectJobsForCurrentLocation,
  jobs =>
    jobs?.reduce((memo: Record<string, string>, job: any) => {
      if (job?.user?.id) {
        memo[job.id] = job.user.id;
      }
      return memo;
    }, {})
);

export const selectDefaultRoleOption = createCachedSelector(
  (
    state: RootState,
    { jobId, isAdding }: { jobId: EntityId; isAdding: boolean }
  ) => {
    const defaultRoleId = selectJobAttribute({
      state,
      attribute: 'defaultRoleId',
      id: jobId,
    });
    const wageRate = selectJobAttribute({
      state,
      attribute: 'wageRate',
      id: jobId,
    });

    const defaultRole = {
      label: 'No Role',
      value: null,
      color: getDefaultShiftColor(selectColorMappingVersion(state) === 1),
      wage: wageRate,
    };

    /** When adding a new shift, use the users default role, else return 'No Role' */
    if (isAdding && defaultRoleId) {
      const defaultRoleEntity = Role.selectEntity(
        state,
        defaultRoleId?.toString()
      );

      if (defaultRoleEntity) {
        defaultRole.value = defaultRoleEntity.attributes.name;
        defaultRole.label = defaultRoleEntity.attributes.name;
        defaultRole.color = defaultRoleEntity.attributes.color;
      }
    }

    return defaultRole;
  },
  defaultRole => defaultRole
)((_, { jobId, isAdding }) => `selectDefaultRoleOption-${jobId}-${isAdding}`);
